/**
 * FormComponents.js
 * 
 * This file contains reusable form components for use in our standard one column form pages. 
 * 
 */

import React from 'react';
import { CharacterLimitIndicator } from "../helpers/CharacterLimitIndicator";
import Tooltip from "../ui/tooltips/Tooltip";

/**
 * InputField Component
 * 
 * A text input field that allows users to enter one line of text with character limit indicator. It can display an optional tooltip, left and right icons, and required indicator.
 * 
 * Props:
 * - label (string, required): The label for the input field.
 * - onChange (function, required): Callback function to handle changes in the input value.
 * - maxChars (number, required): Maximum number of characters allowed in the input.
 * - required (boolean, optional): Indicates if the field is required. Defaults to null.
 * - tooltip (string, optional): Tooltip text to provide additional information. Defaults to null.
 * - iconLeft (JSX, optional): Icon to display on the left side of the label. Defaults to null.
 * - iconRight (JSX, optional): Icon to display on the right side of the label. Defaults to null.
 * - placeholder (string, optional): Placeholder text for the input field. Defaults to null.
 * - value (string, optional): The current value of the input field. Defaults to null.
 */
export const InputField = ({ 
    label, 
    onChange, 
    maxChars, 
    required = null, 
    tooltip = null, 
    iconLeft = null, 
    iconRight = null, 
    placeholder = null, 
    value = null,
    disabled = false
}) => (
    <div style={{ marginBottom: "24px", opacity: disabled ? 0.5 : 1 }}>
        <div className="file-form-label">
            {iconLeft && <span className="input-icon">{iconLeft}</span>}
            {label}
            {required && <span style={{ color: "var(--iNGAGED-Destructive-Color)", marginLeft: "4px", fontWeight: "500" }}>*</span>}
            {iconRight && <span className="input-icon">{iconRight}</span>}
            {tooltip && (
                <Tooltip html={tooltip} placement="right">
                    <i className="bi bi-info-circle" style={{ paddingLeft: "5px", fontSize: "13px", color: "var(--iNGAGED-Primary-Color)" }}></i>
                </Tooltip>
            )}
        </div>
        <input
            value={value || ""}
            onChange={(event) => onChange(event.target.value)}
            className="form-control"
            type="text"
            placeholder={placeholder || ""}
            disabled={disabled}
            style={{ height: "48px", borderRadius: "12px", border: "solid 1px rgba(47, 43, 67, 0.2)", paddingLeft: "16px", paddingRight: "16px", pointerEvents: disabled ? 'none' : 'auto', cursor: disabled ? 'not-allowed' : 'text' }}
        />
        <CharacterLimitIndicator maxChars={maxChars} inputStr={value || ""} />
    </div>
);

/**
 * TextAreaField Component
 * 
 * A text area input field that allows users to enter multi-line text with character limit indicator. It can display an optional tooltip, left and right icons, and required indicator.
 * 
 * Props:
 * - label (string, required): The label for the text area field.
 * - onChange (function, required): Callback function to handle changes in the text area value.
 * - maxChars (number, required): Maximum number of characters allowed in the text area.
 * - tooltip (string, optional): Tooltip text to provide additional information. Defaults to null.
 * - iconLeft (JSX, optional): Icon to display on the left side of the label. Defaults to null.
 * - iconRight (JSX, optional): Icon to display on the right side of the label. Defaults to null.
 * - placeholder (string, optional): Placeholder text for the text area. Defaults to null.
 * - value (string, optional): The current value of the text area. Defaults to null.
 */
export const TextAreaField = ({ 
    label, 
    onChange, 
    maxChars, 
    tooltip = null, 
    iconLeft = null, 
    iconRight = null, 
    placeholder = null, 
    value = null,
    disabled = false
}) => (
    <div style={{ marginBottom: "24px", opacity: disabled ? 0.5 : 1 }}>
        <div className="file-form-label">
            {iconLeft && <span className="input-icon">{iconLeft}</span>}
            {label}
            {iconRight && <span className="input-icon">{iconRight}</span>}
            {tooltip && (
                <Tooltip html={tooltip} placement="right">
                    <i className="bi bi-info-circle" style={{ paddingLeft: "5px", fontSize: "13px", color: "var(--iNGAGED-Primary-Color)" }}></i>
                </Tooltip>
            )}
        </div>
        <textarea
            value={value || ""}
            className="form-control"
            disabled={disabled}
            style={{ height: "96px", borderRadius: "12px", border: "solid 1px rgba(47, 43, 67, 0.2)", resize: "none", padding: "16px", pointerEvents: disabled ? 'none' : 'auto', cursor: disabled ? 'not-allowed' : 'text' }}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder || ""}
        />
        <CharacterLimitIndicator maxChars={maxChars} inputStr={value || ""} />
    </div>
);

/**
 * SwitchField Component
 * 
 * A toggle switch button with a label that allows users to mark it as enabled or disabled. It can display an optional tooltip and left or right icons.
 * 
 * Props:
 * - label (string, required): The label for the switch.
 * - onChange (function, required): Callback function to handle changes in the switch state.
 * - tooltip (string, optional): Tooltip text to provide additional information. Defaults to null.
 * - iconLeft (JSX, optional): Icon to display on the left side of the label. Defaults to null.
 * - iconRight (JSX, optional): Icon to display on the right side of the label. Defaults to null.
 * - value (boolean, optional): The current value of the switch (true for on, false for off). Defaults to null.
 */
export const SwitchField = ({ label, onChange, tooltip = null, iconLeft = null, iconRight = null, checked = false, disabled = false }) => (
    <div style={{ marginBottom: "29px", display: "flex", gap: "10px", opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
        <div className="file-form-label">
            {iconLeft && <span className="me-2">{iconLeft}</span>}
            {label}
            {iconRight && <span className="ms-2">{iconRight}</span>}
            {tooltip && (
                <Tooltip html={tooltip} placement="right">
                    <i className="bi bi-info-circle" style={{ paddingLeft: "5px", fontSize: "13px", color: "var(--iNGAGED-Primary-Color)" }}></i>
                </Tooltip>
            )}
        </div>
        <div className="form-switch form-switch-locked">
            <input
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="form-check-input custom-switch-input"
                type="checkbox"
                disabled={disabled}
            />
        </div>
    </div>
);

/**
 * CheckboxField Component
 * 
 * A checkbox with label that allows users to select or deselect it. It can display an optional tooltip and left or right icons.
 * 
 * Props:
 * - label (string, required): The label for the checkbox.
 * - onChange (function, required): Callback function to handle changes in the checkbox state.
 * - tooltip (string, optional): Tooltip text to provide additional information. Defaults to null.
 * - iconLeft (JSX, optional): Icon to display on the left side of the label. Defaults to null.
 * - iconRight (JSX, optional): Icon to display on the right side of the label. Defaults to null.
 * - value (boolean, optional): The current value of the checkbox (true for checked, false for unchecked). Defaults to null.
 */
export const CheckboxField = ({ label, onChange, tooltip = null, iconLeft = null, iconRight = null, checked = false, disabled = false }) => (
    <div style={{ marginBottom: "26px", display: "flex", gap: "10px", opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
        <div className="d-flex align-items-center">
            {iconLeft && <span className="me-2">{iconLeft}</span>}
            <input
                type="checkbox"
                className="form-check-input custom-checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                disabled={disabled}
            />
            <span style={{ color: "#2F2B43", fontWeight: "500", fontSize: "16px" }}>{label}</span>
            {iconRight && <span className="ms-2">{iconRight}</span>}
            {tooltip && (
                <Tooltip html={tooltip} placement="right">
                    <i className="bi bi-info-circle" style={{ marginLeft: "5px", fontSize: "13px", color: "var(--iNGAGED-Primary-Color)" }}></i>
                </Tooltip>
            )}
        </div>
    </div>
);
